import { visivaTheme } from './theme'
import { requireApiBaseURL, requireProductReturnOrigin, requireVisivaSiteCode } from '../env'

import type { BrandConfig } from '@checkout/themes'

const logoLight = '/assets/visiva/logo-light.png'
const logoDark = '/assets/visiva/logo-light.png'
const faviconLight = '/assets/visiva/favicon.png'
const faviconDark = '/assets/visiva/favicon-dark.png'

export function createVisivaConfig(): BrandConfig {
  const apiBaseURL = requireApiBaseURL('visiva')
  const productReturnOrigin = requireProductReturnOrigin('visiva')
  const siteCode = requireVisivaSiteCode()
  const isNewSite = siteCode === 'visiva_new'
  const legalOrigin = isNewSite ? productReturnOrigin : 'https://www.visiva.ai'

  return {
    id: 'visiva',
    name: 'Visiva',

    theme: visivaTheme,

    logo: {
      light: logoLight,
      dark: logoDark,
      width: 134,
      height: 40,
    },

    header: {
      languageSwitcherSize: 'md',
      securityBadges: [
        { src: '/assets/badges/pci-dss.png', alt: 'PCI DSS', width: 78 },
        { src: '/assets/badges/ssl-secure.png', alt: 'SSL Secure', width: 74 },
        { src: '/assets/badges/verified.png', alt: 'Verified', width: 62 },
      ],
    },

    favicon: {
      light: faviconLight,
      dark: faviconDark,
    },

    api: {
      baseURL: apiBaseURL,
      productCode: 'visiva',
      timeout: 30000,
    },

    features: {
      expressCheckout: true,
      couponCode: true,
      guestCheckout: true,
      showProductImages: true,
      darkModeToggle: false,
      showCurrencySelector: true,
    },

    locale: {
      defaultCurrency: 'USD',
      defaultLanguage: 'en',
      supportedLanguages: ['en', 'zh', 'es'],
    },

    legal: {
      termsUrl: `${legalOrigin}/terms`,
      privacyUrl: `${legalOrigin}/privacy-policy`,
      refundPolicyUrl: `${legalOrigin}/refund-policy`,
      subscriptionTermsUrl: `${legalOrigin}/subscription-terms`,
    },

    footer: {
      disclaimerTitle: 'footer.visiva.disclaimerTitle',
      disclaimer: 'footer.visiva.disclaimer',
      copyrightTemplate: 'footer.visiva.copyright',
    },

    termsText: {
      template: 'terms.visiva.agreement',
      privacyLinkText: 'terms.visiva.privacyPolicy',
      refundLinkText: 'terms.visiva.refundPolicy',
      ...(isNewSite
        ? {
            cryptocurrencyNotice: {
              template: 'terms.visiva.cryptocurrencyNotice',
              refundLinkText: 'terms.visiva.cryptocurrencyPayments',
              refundPolicySectionId: 'cryptocurrency-payments',
            },
          }
        : {}),
    },

    contact: {
      email: isNewSite ? 'support@visivaai.com' : 'support@visiva.ai',
      supportUrl: isNewSite ? `${legalOrigin}/contact-us` : 'https://help.visiva.ai',
    },

    embeddedPayments: {
      airwallex: {
        env: (import.meta.env.VITE_AIRWALLEX_ENV as 'prod' | 'demo') || 'prod',
        applePay: {
          buttonType: 'buy',
          buttonColor: 'black',
        },
        googlePay: {
          buttonType: 'buy',
          buttonColor: 'black',
        },
      },
    },

    productReturnOrigin,

    // GA/GTM 埋点配置
    analytics: {
      enabled:
        import.meta.env.VITE_ANALYTICS_ENABLED === 'true' ||
        (typeof window !== 'undefined' && window.location.hostname === 'checkout.visiva.ai'),
      gtmId: isNewSite ? 'GTM-NHVT2RJ8' : 'GTM-W9LMJK9C',
      ...(isNewSite ? { businessEventContract: 'ga4_ecommerce_v1' as const } : {}),
      // 模块名称前缀，用于区分不同品牌的事件
      modulePrefix: 'vs_checkout_',
    },
  }
}
